import {AnimatePresence, m} from 'framer-motion';
import {Fragment} from 'react';
import {opacityAnimation} from '@ui/animation/opacity-animation';
import {Skeleton} from '@ui/skeleton/skeleton';
import {useProducts} from '@common/billing/pricing-table/use-products';
import {Product} from '@common/billing/product';
import {
  findBestPrice,
  UpsellBillingCycle,
} from '@common/billing/pricing-table/find-best-price';
import {useAuth} from '@common/auth/use-auth';
import clsx from 'clsx';
import {Chip} from '@ui/forms/input-field/chip-field/chip';
import {Trans} from '@ui/i18n/trans';
import {FormattedPrice} from '@common/billing/formatted-price';
import {Button} from '@ui/buttons/button';
import {Link} from 'react-router-dom';
import {setInLocalStorage} from '@ui/utils/hooks/local-storage';
import {ProductFeatureList} from '@common/billing/pricing-table/product-feature-list';
import {useIsMobileMediaQuery} from '@ui/utils/hooks/is-mobile-media-query';
import {Swiper, SwiperSlide} from 'swiper/react';
import {Pagination} from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

interface PricingTableProps {
  selectedCycle: UpsellBillingCycle;
  className?: string;
  productLoader?: string;
  role?: string;
}

const mobilePricingSwiperBreakpoints = {
  0: {
    slidesPerView: 1.18,
    spaceBetween: 8,
  },
  380: {
    slidesPerView: 1.22,
    spaceBetween: 10,
  },
  520: {
    slidesPerView: 1.28,
    spaceBetween: 12,
  },
};

export function PricingTable({
  selectedCycle,
  className,
  productLoader,
  role,
}: PricingTableProps) {
  const resolvedRole = role || 'user';
  const isMobile = !!useIsMobileMediaQuery();
  const query = useProducts(productLoader, resolvedRole);

  return (
    <div
      className={clsx(
        isMobile
          ? 'pricing-table-mobile-bleed w-full'
          : 'flex flex-col items-stretch gap-24 overflow-x-auto overflow-y-visible pb-20 md:flex-row md:justify-center',
        className,
      )}
    >
      <AnimatePresence initial={false} mode="wait">
        {query.data ? (
          <PlanList
            key={isMobile ? 'plan-list-mobile' : 'plan-list-desktop'}
            plans={query.data.products}
            selectedPeriod={selectedCycle}
            role={resolvedRole}
            isMobile={isMobile}
          />
        ) : (
          <SkeletonLoader
            key={isMobile ? 'skeleton-loader-mobile' : 'skeleton-loader-desktop'}
            isMobile={isMobile}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

interface PlanListProps {
  plans: Product[];
  selectedPeriod: UpsellBillingCycle;
  role?: string;
  isMobile?: boolean;
}
function PlanList({plans, selectedPeriod, role, isMobile}: PlanListProps) {
  const {isLoggedIn, isSubscribed, user} = useAuth();
  const visiblePlans = plans.filter(plan =>
    !plan.hidden && (plan.free || !!findBestPrice(selectedPeriod, plan.prices))
  );

  if (!visiblePlans.length) {
    return null;
  }

  if (isMobile) {
    const initialSlide = Math.max(
      visiblePlans.findIndex(plan => plan.recommended),
      0,
    );

    return (
      <Swiper
        modules={[Pagination]}
        autoHeight
        grabCursor={visiblePlans.length > 1}
        centeredSlides={visiblePlans.length > 1}
        initialSlide={initialSlide}
        pagination={visiblePlans.length > 1 ? {clickable: true} : false}
        breakpoints={mobilePricingSwiperBreakpoints}
        className="pricing-table-swiper w-full"
      >
        {visiblePlans.map(plan => (
          <SwiperSlide key={plan.id} className="pricing-plan-slide pt-4">
            <PlanCard
              plan={plan}
              selectedPeriod={selectedPeriod}
              role={role}
              isLoggedIn={isLoggedIn}
              isSubscribed={isSubscribed}
              hasUsedTrial={!!user?.has_used_trial}
              className="w-full"
            />
          </SwiperSlide>
        ))}
      </Swiper>
    );
  }

  return (
    <Fragment>
      {visiblePlans.map((plan, index) => (
        <PlanCard
          key={plan.id}
          plan={plan}
          selectedPeriod={selectedPeriod}
          role={role}
          isLoggedIn={isLoggedIn}
          isSubscribed={isSubscribed}
          hasUsedTrial={!!user?.has_used_trial}
          className={clsx(
            'w-full md:min-w-240 md:max-w-350',
            index === 0 && 'ml-auto',
            index === visiblePlans.length - 1 && 'mr-auto',
          )}
        />
      ))}
    </Fragment>
  );
}

interface PlanCardProps {
  plan: Product;
  selectedPeriod: UpsellBillingCycle;
  role?: string;
  isLoggedIn: boolean;
  isSubscribed: boolean;
  hasUsedTrial: boolean;
  className?: string;
}

function PlanCard({
  plan,
  selectedPeriod,
  role,
  isLoggedIn,
  isSubscribed,
  hasUsedTrial,
  className,
}: PlanCardProps) {
  const price = findBestPrice(selectedPeriod, plan.prices);
  const hasTrial = plan.trial_days && plan.trial_days > 0 && !hasUsedTrial;

  let upgradeRoute;
  if (!isLoggedIn) {
    upgradeRoute = `/register?redirectFrom=pricing/${role}`;
  }
  if (isSubscribed) {
    upgradeRoute = `/change-plan/${role}/${plan.id}/${price?.id}/confirm`;
  }
  if (isLoggedIn && !plan.free) {
    upgradeRoute = `/checkout/${role}/${plan.id}/${price?.id}`;
  }

  return (
    <m.div
      {...opacityAnimation}
      className={clsx(
        'flex h-full flex-col rounded-panel border bg px-28 py-28 shadow-lg',
        plan.recommended && 'border-primary/80',
        className,
      )}
    >
      <div className="mb-32">
        <div className="mb-12 text-xl font-semibold">
          <Trans message={plan.name} />
        </div>
        <div className="text-sm text-muted">
          <Trans message={plan.description} />
        </div>
      </div>

      <div className="flex flex-1 flex-col">
        {price ? (
          <FormattedPrice
            priceClassName="font-bold text-4xl"
            periodClassName="text-muted text-xs"
            variant="separateLine"
            price={price}
          />
        ) : (
          <div className="text-4xl font-bold">
            <Trans message="Free" />
          </div>
        )}

        <div className="mt-40 mb-20 flex flex-col items-center justify-center gap-6 overflow-hidden py-6 font-medium">
          {plan.recommended && (
            <div>
              <Chip
                size="md"
                radius="rounded"
                color="chip"
                className="w-min"
              >
                <Trans message="Most popular" />
              </Chip>
            </div>
          )}
          {hasTrial && (
            <div>
              <Chip
                size="md"
                radius="rounded"
                color="positive"
                className="w-min"
              >
                <Trans
                  message="Try it free for :day days"
                  values={{day: plan.trial_days}}
                />
              </Chip>
            </div>
          )}
        </div>

        <div className="mt-40">
          <Button
            variant={plan.recommended ? 'flat' : 'outline'}
            color="primary"
            className="w-full"
            size="md"
            elementType={upgradeRoute ? Link : undefined}
            disabled={!upgradeRoute}
            onClick={() => {
              if (isLoggedIn || !price) return;
              setInLocalStorage('be.onboarding.selected', {
                role,
                productId: plan.id,
                priceId: price.id,
              });
            }}
            to={upgradeRoute}
          >
            {plan.free ? (
              <Trans message="Get started" />
            ) : (
              <Trans message="Subscribe now" />
            )}
          </Button>
        </div>

        <ProductFeatureList product={plan} />
      </div>
    </m.div>
  );
}

interface SkeletonLoaderProps {
  isMobile?: boolean;
}

function SkeletonLoader({isMobile}: SkeletonLoaderProps) {
  if (isMobile) {
    return (
      <Swiper
        modules={[Pagination]}
        centeredSlides
        pagination={{clickable: true}}
        breakpoints={mobilePricingSwiperBreakpoints}
        className="pricing-table-swiper w-full"
      >
        <SwiperSlide className="pricing-plan-slide pt-4">
          <PlanSkeleton key="skeleton-1" className="w-full" />
        </SwiperSlide>
        <SwiperSlide className="pricing-plan-slide pt-4">
          <PlanSkeleton key="skeleton-2" className="w-full" />
        </SwiperSlide>
        <SwiperSlide className="pricing-plan-slide pt-4">
          <PlanSkeleton key="skeleton-3" className="w-full" />
        </SwiperSlide>
      </Swiper>
    );
  }

  return (
    <Fragment>
      <PlanSkeleton key="skeleton-1" className="w-full" />
      <PlanSkeleton key="skeleton-2" className="w-full" />
      <PlanSkeleton key="skeleton-3" className="w-full" />
    </Fragment>
  );
}

interface PlanSkeletonProps {
  className?: string;
}

function PlanSkeleton({className}: PlanSkeletonProps) {
  return (
    <m.div
      {...opacityAnimation}
      className={clsx(
        'rounded-panel border px-28 py-90 shadow-lg md:max-w-350',
        className,
      )}
    >
      <Skeleton className="my-10" />
      <Skeleton className="mb-40" />
      <Skeleton className="mb-40 h-30" />
      <Skeleton className="mb-40 h-40" />
      <Skeleton className="mb-20" />
      <Skeleton />
      <Skeleton />
    </m.div>
  );
}
